import { useEffect, useMemo, useRef } from 'react';
import {
  selectBundlePageIds,
  selectBundleViewedPageIds,
  selectBundleViewedPageIdsSet,
  selectSharedDataActiveBundleType,
  selectViewportPageIndexInStore,
  setBundleViewedPageIds,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { debounce } from '@/utils';

const DEBOUNCE_PAGE_VIEW_TIME_MS = 2_000;

export const useThumbnailsViewedStatus = () => {
  const dispatch = useAppDispatch();

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const thumbnailsViewedStatusPageIds = useAppSelector(selectBundleViewedPageIds);
  const thumbnailsViewedStatusPageIdsSet = useAppSelector(selectBundleViewedPageIdsSet);
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);

  // Hold the latest values in a ref so `markAsPageViewed` can stay stable
  // across renders. Otherwise its identity changed every time a page was
  // marked viewed (because `thumbnailsViewedStatusPageIds` is a dep), the
  // effect below would re-fire and restart the 2s timer for the current page,
  // and the previous debounce's pending setTimeout would still be alive in its
  // own closure — occasionally firing with stale state.
  const latestRef = useRef({
    activeBundleType,
    bundlePageIds,
    thumbnailsViewedStatusPageIds,
  });
  latestRef.current = {
    activeBundleType,
    bundlePageIds,
    thumbnailsViewedStatusPageIds,
  };

  const markAsPageViewed = useMemo(
    () =>
      debounce((viewportPageIndex: number) => {
        const { activeBundleType, bundlePageIds, thumbnailsViewedStatusPageIds } = latestRef.current;
        const viewportPageId = bundlePageIds[viewportPageIndex];

        if (viewportPageId && !thumbnailsViewedStatusPageIds.includes(viewportPageId) && activeBundleType) {
          dispatch(
            setBundleViewedPageIds({
              key: activeBundleType,
              data: [...thumbnailsViewedStatusPageIds, viewportPageId],
            }),
          );
        }
      }, DEBOUNCE_PAGE_VIEW_TIME_MS),
    [dispatch],
  );

  useEffect(() => {
    markAsPageViewed(viewportPageIndexInStore);
  }, [viewportPageIndexInStore, markAsPageViewed]);

  return { thumbnailsViewedStatusPageIds, thumbnailsViewedStatusPageIdsSet };
};
